<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatDateShort } from '@shared/utils'
import ListingStepTracker from './ListingStepTracker.vue'
import { stageGuidanceOf } from '../../constants/listingRequestLifecycle'
import type { ListingRequestSummary } from '../../types/listingRequestTypes'

// One row in the Sell Request ledger. Presentational — property identity on the
// left, the inline lifecycle tracker in the middle, "View Details" on the right.
// Emits `view` with the id.
const props = defineProps<{ request: ListingRequestSummary }>()
const emit = defineEmits<{ (e: 'view', id: string): void }>()

const location = computed(
  () => [props.request.buildingName, props.request.locationArea].filter(Boolean).join(', ') || '—',
)

const listedOn = computed(() =>
  formatDateShort(props.request.submittedAt ?? props.request.createdAt),
)

const trackerNote = computed(() => stageGuidanceOf(props.request.stage, props.request.stageState))
</script>

<template>
  <Box is="article" flex align="center" gap="4" class="listing-row">
    <Box direction="col" gap="2" class="listing-row__identity">
      <Text is="h3" type="body" weight="semibold" truncate>
        {{ request.propertyName ?? 'Untitled property' }}
      </Text>
      <Box flex align="center" gap="3" class="listing-row__meta">
        <Box
          flex
          align="center"
          gap="1"
          class="listing-row__meta-item listing-row__meta-item--location"
        >
          <PiIcon name="map-marker" class="listing-row__meta-icon" />
          <Text type="caption" color="muted" truncate>{{ location }}</Text>
        </Box>
        <Box flex align="center" gap="1" class="listing-row__meta-item">
          <PiIcon name="calendar" class="listing-row__meta-icon" />
          <Text type="caption" color="muted">{{ listedOn }}</Text>
        </Box>
      </Box>
    </Box>

    <Box grow class="listing-row__tracker">
      <ListingStepTracker
        :current-step="request.stageStep"
        :state="request.stageState"
        :note="trackerNote"
      />
    </Box>

    <Button
      label="View Details"
      variant="outline"
      size="small"
      class="listing-row__action"
      @click="emit('view', request.id)"
    />
  </Box>
</template>

<style scoped>
.listing-row {
  padding: 1.25rem;
  border: 1px solid var(--p-content-border-color);
  border-left: 3px solid var(--p-primary-300);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
}

/* Narrower than the tracker needs to be: the identity block truncates cleanly
   (title + location already ellipsis), the five-step rail does not. Widening
   this column is what pushed "Listed" onto a second line. */
.listing-row__identity {
  flex: 0 0 auto;
  width: 256px;
  min-width: 0;
  padding-right: 1rem;
}

/* The column is fixed, so the meta line has to give: without these the flex
   items keep their `min-width: auto` intrinsic width and a long address pushes
   the date out of the column and under the tracker's first pill. The location
   shrinks and ellipsises; the date never does. */
.listing-row__meta {
  min-width: 0;
}

.listing-row__meta-item {
  flex: 0 0 auto;
  min-width: 0;
}

.listing-row__meta-item--location {
  flex-shrink: 1;
}

.listing-row__meta-icon {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--p-text-muted-color);
}

.listing-row__tracker {
  min-width: 0;
}

.listing-row__action {
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .listing-row {
    flex-direction: column;
    align-items: stretch;
  }

  .listing-row__identity {
    width: 100%;
    padding-right: 0;
    border-right: none;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--p-content-border-color);
  }

  .listing-row__action {
    align-self: flex-start;
  }
}
</style>
